import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TALLY_STATUS } from '@adisys/shared';
import { api, ApiRequestError } from '../../../lib/api';
import { MetaBadge, Notice, errorText, inr2, qtyLabel } from '../../../lib/crm';
import { Button, ErrorState, Field, Input, Modal, Skeleton, Textarea, useToast } from '../../../components/ui';

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** Raise a GST invoice for what has been delivered (services as performed). */
export function InvoiceModal({ orderId, open, onClose, onDone }: {
  orderId: string | null; open: boolean; onClose: () => void; onDone?: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const order = useQuery({
    queryKey: ['crm', 'order', orderId], enabled: open && !!orderId,
    queryFn: () => api.get(`/crm/orders/${orderId}`),
  });
  const [qty, setQty] = useState<Record<string, string>>({});
  const [f, setF] = useState({ invoiceDate: '', dueDate: '', note: '' });
  const [done, setDone] = useState<{ id: string; invoiceNo: string; tallyStatus: any } | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => { if (open) { setQty({}); setDone(null); setError(null); } }, [open, orderId]);

  const items: any[] = order.data?.items ?? [];
  const billable = (i: any) => (i.kind === 'product' ? Number(i.deliveredQty) : Number(i.quantity)) - Number(i.invoicedQty);
  const chosen = items.map((i) => ({ i, q: Number(qty[i.id] || 0) })).filter((x) => x.q > 0);
  const taxable = r2(chosen.reduce((s, x) => s + x.q * Number(x.i.netUnitPrice), 0));
  const gst = r2(chosen.reduce((s, x) => s + r2(x.q * Number(x.i.netUnitPrice)) * Number(x.i.gstRate) / 100, 0));

  const save = useMutation({
    mutationFn: () => api.post(`/crm/orders/${orderId}/invoices`, {
      items: chosen.map((x) => ({ orderItemId: x.i.id, quantity: x.q })),
      invoiceDate: f.invoiceDate || undefined, dueDate: f.dueDate || undefined, note: f.note || undefined,
    }),
    onSuccess: (r: any) => {
      setDone(r); toast.success('Invoice issued', r.invoiceNo);
      void qc.invalidateQueries({ queryKey: ['crm'] }); onDone?.();
    },
    onError: (e) => { setError(e); toast.error('Could not issue the invoice', errorText(e)); },
  });
  const fe = (k: string) => (error instanceof ApiRequestError ? error.fieldError(k) : undefined);

  return (
    <Modal open={open} onClose={onClose} size="lg" title="Raise GST invoice"
      description="Goods are billed once delivered; installation and service lines can be billed as they are performed."
      footer={done ? <Button onClick={onClose}>Close</Button> : (
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!chosen.length} onClick={() => save.mutate()}>Issue invoice</Button>
        </>
      )}>
      {done ? (
        <div className="space-y-3 text-sm">
          <p>Invoice <strong>{done.invoiceNo}</strong> issued.</p>
          <p className="flex items-center gap-2">Tally: <MetaBadge meta={TALLY_STATUS} value={done.tallyStatus} />
            {done.tallyStatus === 'queued' && <span className="text-xs text-ink-500">Voucher is ready; no Tally endpoint is configured yet.</span>}</p>
          <Link className="font-medium text-brand-600 hover:underline" to={`/crm/finance/invoices/${done.id}`}>Open invoice</Link>
        </div>
      ) : order.isLoading ? <Skeleton className="h-40 w-full" /> : order.isError ? <ErrorState error={order.error} onRetry={() => order.refetch()} /> : (
        <div className="space-y-4">
          <div className="overflow-x-auto rounded-lg ring-1 ring-line">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead className="bg-sunken/60 text-xs text-ink-600">
                <tr><th className="px-3 py-2">Line</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">GST</th>
                  <th className="px-3 py-2 text-right">Billable</th><th className="w-28 px-3 py-2 text-right">Bill now</th></tr>
              </thead>
              <tbody className="divide-y divide-line">
                {items.map((i) => {
                  const b = billable(i);
                  return (
                    <tr key={i.id}>
                      <td className="px-3 py-2"><p className="font-medium text-ink-900">{i.description}</p>
                        <p className="text-xs text-ink-500">{i.kind === 'product' ? `Delivered ${qtyLabel(i.deliveredQty)} of ${qtyLabel(i.quantity)}` : 'Service'} · billed {qtyLabel(i.invoicedQty)}</p></td>
                      <td className="tabular px-3 py-2 text-right">{inr2(i.netUnitPrice)}</td>
                      <td className="tabular px-3 py-2 text-right">{qtyLabel(i.gstRate)}%</td>
                      <td className="tabular px-3 py-2 text-right">{qtyLabel(b)}</td>
                      <td className="px-3 py-2">
                        <Input type="number" min={0} max={b} step="any" disabled={b <= 0} className="text-right"
                          aria-label={`Bill quantity for ${i.description}`}
                          value={qty[i.id] ?? ''} onChange={(e) => setQty({ ...qty, [i.id]: e.target.value })} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <dl className="tabular ml-auto w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-ink-500">Taxable value</dt><dd>{inr2(taxable)}</dd></div>
            <div className="flex justify-between"><dt className="text-ink-500">GST</dt><dd>{inr2(gst)}</dd></div>
            <div className="flex justify-between border-t border-line pt-1 font-semibold text-ink-900"><dt>Invoice total</dt><dd>{inr2(taxable + gst)}</dd></div>
          </dl>
          {error instanceof ApiRequestError && !fe('dueDate') && <Notice tone="danger">{error.message}</Notice>}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Invoice date"><Input type="date" value={f.invoiceDate} onChange={(e) => setF({ ...f, invoiceDate: e.target.value })} /></Field>
            <Field label="Due date" error={fe('dueDate')} hint="Defaults to the standard credit period.">
              <Input type="date" value={f.dueDate} onChange={(e) => setF({ ...f, dueDate: e.target.value })} /></Field>
          </div>
          <Field label="Note"><Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        </div>
      )}
    </Modal>
  );
}
